import { create } from "zustand";

interface TranscriptSearchState {
	/** 検索欄を表示しているか。 */
	open: boolean;
	query: string;
	/** 履歴に見つかった一致箇所の数。 */
	matchCount: number;
	/** そのうち、いま辿っている箇所 (0 起点)。一致が無いときは 0。 */
	activeIndex: number;
	/**
	 * 検索欄を開く操作のたびに進むカウンタ。既に開いているときも入力欄へフォーカスを
	 * 戻せるよう、`open` とは別に「開く操作があった」ことを表す。
	 */
	focusRequestId: number;
	openSearch: () => void;
	closeSearch: () => void;
	setQuery: (query: string) => void;
	/**
	 * 一致数を更新する。辿っている箇所は、範囲内であればそのまま保つ。
	 * 古い履歴が先頭へ足されたときは、その中の一致数を insertedBefore に渡すと同じ箇所を辿り続ける。
	 */
	setMatchCount: (matchCount: number, insertedBefore?: number) => void;
	/** 次 (1) / 前 (-1) の一致へ移動する。端では反対側へ回り込む。 */
	stepMatch: (direction: 1 | -1) => void;
	reset: () => void;
}

const INITIAL = {
	open: false,
	query: "",
	matchCount: 0,
	activeIndex: 0,
};

export const useTranscriptSearchStore = create<TranscriptSearchState>()((set) => ({
	...INITIAL,
	focusRequestId: 0,
	openSearch: () => set((state) => ({ open: true, focusRequestId: state.focusRequestId + 1 })),
	closeSearch: () => set({ open: false }),
	// 入力が変わればそれまで辿っていた箇所は無効なので、先頭の一致から辿り直す。
	setQuery: (query) => set({ query, activeIndex: 0 }),
	// ストリーミング中は本文が伸び続けて一致数も増減するため、位置は範囲内に収めるだけにする
	// (毎回先頭へ戻すと、応答が届くたびユーザーの辿っている位置が飛んでしまう)。
	setMatchCount: (matchCount, insertedBefore = 0) =>
		set((state) => ({
			matchCount,
			activeIndex: Math.min(
				state.matchCount > 0 ? state.activeIndex + insertedBefore : state.activeIndex,
				Math.max(matchCount - 1, 0),
			),
		})),
	stepMatch: (direction) =>
		set((state) => {
			if (state.matchCount === 0) return {};
			return {
				activeIndex: (state.activeIndex + direction + state.matchCount) % state.matchCount,
			};
		}),
	// query も含めて初期化する。別チャットへ切り替えたら前チャットの検索語は無効。
	reset: () => set({ ...INITIAL }),
}));
